/**
 * /perfil/totp — tela de cadastro do TOTP (GUPM-104, Fase 2.5).
 *
 * Fluxo:
 *   1. Usuário entra na tela
 *   2. Sistema chama provision-totp (Edge Function) que gera secret + otpauth URI
 *   3. Tela mostra QR code (otpauth://) — usuário escaneia com Microsoft
 *      Authenticator, Google, Authy ou qualquer app TOTP compatível
 *   4. App passa a mostrar uma entrada "GeoGuard UPM - [nome]" com código rotativo
 *   5. Usuário digita o código atual pra confirmar enrollment
 *   6. Front chama verify-totp pra validar; se ok, chama set_my_totp (RPC)
 *      persistindo o secret no engineers.totp_secret_base32
 *
 * Recadastro: chamar provision-totp novamente substitui o secret antigo
 * (após confirmação). Útil pra trocar de dispositivo.
 */

import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Link } from 'react-router-dom';
import { useDataSources } from '@/services/datasources';
import {
  useConfirmTotpEnrollment,
  useCurrentEngineer,
  useProvisionTotp,
} from './hooks';

export default function TotpEnrollmentPage() {
  const { data: engineer } = useCurrentEngineer();
  const provision = useProvisionTotp();
  const confirmEnroll = useConfirmTotpEnrollment();
  const { shiftHandover } = useDataSources();

  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [enrolledOk, setEnrolledOk] = useState(false);

  async function startEnrollment() {
    setPageError(null);
    setEnrolledOk(false);
    try {
      const result = await provision.mutateAsync();
      const svg = await QRCode.toString(result.otpauthUri, { type: 'svg', margin: 1, width: 240 });
      setQrSvg(svg);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Falha ao gerar secret');
    }
  }

  async function handleVerifyAndSave() {
    if (!provision.data) return;
    if (!engineer) {
      setPageError('Engineer não carregou — recarregue a página.');
      return;
    }
    if (!/^\d{6}$/.test(verifyCode)) {
      setPageError('Código deve ter 6 dígitos.');
      return;
    }
    setVerifying(true);
    setPageError(null);
    try {
      // 1) Server-side gatekeep: valida o secret candidato contra o código
      // informado, sem persistir nada. Modo enrollment é restrito a self
      // (Edge Function rejeita se targetEngineerId != user autenticado) e
      // não conta pra counter de lockout. Audit log grava
      // shift_totp_enrollment_verified / _failed.
      const verified = await shiftHandover.verifyEnrollmentTotp(
        engineer.id,
        verifyCode,
        provision.data.secret,
      );
      if (!verified.success) {
        const msg =
          verified.error === 'enrollment_self_only'
            ? 'Você só pode cadastrar TOTP da sua própria conta.'
            : 'Código incorreto. Confira o código atual no app autenticador e tente novamente.';
        setPageError(msg);
        return;
      }

      // 2) Server confirmou — agora seguro persistir o secret.
      // Re-cadastro: o secret antigo só é sobrescrito após a confirmação
      // acima, então o app autenticador atual nunca fica em estado
      // inconsistente com o banco.
      await confirmEnroll.mutateAsync(provision.data.secret);
      setEnrolledOk(true);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Falha ao confirmar enrollment');
    } finally {
      setVerifying(false);
    }
  }

  // Limpa QR se o engineer mudar
  useEffect(() => {
    setQrSvg(null);
    setVerifyCode('');
    setEnrolledOk(false);
  }, [engineer?.id]);

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div>
        <p className="text-xs uppercase tracking-wide text-jf-dim">Perfil</p>
        <h1 className="text-2xl font-bold text-white">Autenticador (TOTP)</h1>
        <p className="text-sm text-jf-muted">
          Cadastre um código rotativo de 6 dígitos no seu Microsoft Authenticator (ou Google,
          Authy, 1Password). Usado para confirmar troca de plantão e outras ações sensíveis.
          Funciona offline, é específico deste app e diferente do seu login Microsoft.
        </p>
      </div>

      {!qrSvg && !enrolledOk && (
        <div className="rounded-lg border border-jf-border bg-jf-surface p-4">
          <ol className="mb-4 space-y-2 text-sm text-jf-muted">
            <li>
              <span className="font-bold text-jf-yellow">1.</span> Abra o Microsoft Authenticator no
              seu celular
            </li>
            <li>
              <span className="font-bold text-jf-yellow">2.</span> Toque em "Adicionar conta" →
              "Outra (Google, Facebook, etc.)"
            </li>
            <li>
              <span className="font-bold text-jf-yellow">3.</span> Volte aqui e clique em "Gerar QR"
              abaixo
            </li>
            <li>
              <span className="font-bold text-jf-yellow">4.</span> Escaneie o QR com a câmera do app
            </li>
            <li>
              <span className="font-bold text-jf-yellow">5.</span> Digite o código atual no campo
              que vai aparecer
            </li>
          </ol>
          <button
            onClick={startEnrollment}
            disabled={provision.isPending}
            className="rounded-md bg-jf-yellow px-4 py-2 text-sm font-bold text-black hover:brightness-110 disabled:opacity-50"
          >
            {provision.isPending ? 'Gerando…' : 'Gerar QR'}
          </button>
        </div>
      )}

      {qrSvg && !enrolledOk && (
        <div className="rounded-lg border border-jf-border bg-jf-surface p-4">
          <p className="mb-3 text-xs uppercase tracking-wide text-jf-dim">
            Escaneie no app autenticador
          </p>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <img
              src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(qrSvg)}`}
              className="rounded-md bg-white p-2 self-start"
              alt="QR code TOTP — escaneie no Microsoft Authenticator"
              width={240}
              height={240}
            />
            <div className="flex-1 space-y-3">
              <details className="text-xs text-jf-muted">
                <summary className="cursor-pointer hover:text-jf-yellow">
                  Não consegue escanear? Inserir secret manual
                </summary>
                <pre className="mt-2 select-all rounded bg-jf-bg p-2 font-mono text-[11px] text-jf-yellow break-all whitespace-pre-wrap">
                  {provision.data?.secret}
                </pre>
              </details>
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-jf-dim mb-1">
                  Código atual do app (6 dígitos)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full max-w-xs rounded-md border border-jf-border bg-jf-surface2 px-3 py-2 text-center text-xl tracking-[0.4em] text-white outline-none focus:border-jf-yellow"
                />
              </div>
              <button
                onClick={handleVerifyAndSave}
                disabled={verifying || verifyCode.length !== 6}
                className="rounded-md bg-jf-yellow px-4 py-2 text-sm font-bold text-black hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {verifying ? 'Validando…' : 'Confirmar cadastro'}
              </button>
            </div>
          </div>
        </div>
      )}

      {enrolledOk && (
        <div className="rounded-lg border border-green-500/40 bg-green-500/10 p-4 text-sm text-green-200">
          <p className="font-bold text-green-300">✅ Autenticador cadastrado!</p>
          <p className="mt-2">
            Você já pode operar a troca de plantão. O código rotativo aparece no app a cada 30
            segundos e funciona offline.
          </p>
          <div className="mt-3 flex gap-2">
            <Link
              to="/dashboard"
              className="rounded-md bg-jf-yellow px-4 py-2 text-xs font-bold text-black hover:brightness-110"
            >
              Ir para o Dashboard
            </Link>
            <button
              onClick={() => {
                setQrSvg(null);
                setEnrolledOk(false);
                provision.reset();
              }}
              className="rounded-md border border-jf-border px-4 py-2 text-xs text-jf-muted hover:bg-jf-border"
            >
              Re-cadastrar (novo dispositivo)
            </button>
          </div>
        </div>
      )}

      {pageError && (
        <div
          role="alert"
          className="rounded-md border border-red-500 bg-red-500/10 px-3 py-2 text-sm text-red-300"
        >
          {pageError}
        </div>
      )}
    </div>
  );
}
